import React from 'react';
import Header from '../Components/Header';
import MainContainer from '../Components/MainContainer';
import ErrorModal from '../Components/Modal/ErrorModal';
import Modal from '../Components/Modal/Modal';
import SuccessModal from '../Components/Modal/SuccessModal';
import { PhoneField } from '../Components/PhoneField';
import SelectInput from '../Components/SelectInput'; // still used for idType, state, country
import TextInput from '../Components/TextInput';
import { ApiInstance, ApiRoutes } from '../Utils/ApiInstance';
import data from '../Utils/Data';
import { validateInvoiceForm } from '../Validators/invoiceValidator';
import './InvoiceForm.css';

function InvoiceForm({ receiptData, onBack }) {
  const phoneRef = React.useRef(null);

  // Safety check for receiptData
  if (!receiptData) {
    return <div>Loading...</div>;
  }

  // Receipt data from step 1
  const { receiptNo, isQrMode, token } = receiptData;

  // Form event handlers
  const handleIdChange = e => {
    let value = e.target.value;

    // Auto-format NRIC with dashes (XXXXXX-XX-XXXX)
    if (idType?.label === 'NRIC' || idType?.value === 'NRIC') {
      // Remove any existing dashes and non-digits
      value = value.replace(/[^0-9]/g, '');

      // Apply formatting: 6 digits - 2 digits - 4 digits
      if (value.length <= 6) {
        // First part: up to 6 digits
        setId(value);
      } else if (value.length <= 8) {
        // Add first dash after 6 digits
        setId(value.slice(0, 6) + '-' + value.slice(6));
      } else if (value.length <= 12) {
        // Add both dashes: 6-2-4 format
        setId(value.slice(0, 6) + '-' + value.slice(6, 8) + '-' + value.slice(8, 12));
      } else {
        // Limit to 12 digits total
        setId(value.slice(0, 6) + '-' + value.slice(6, 8) + '-' + value.slice(8, 12));
      }
    } else {
      // For other ID types, just set the value as-is
      setId(value);
    }
  };
  const handleTaxNoChange = e => setTaxNo(e.target.value);
  const handleNameChange = e => setName(e.target.value);
  const handleEmailChange = e => setEmail(e.target.value);
  // Removed handlers: confirm email, old business reg no, sst no, msic
  const handleAddress1Change = e => setAddress1(e.target.value);
  const handleAddress2Change = e => setAddress2(e.target.value);
  const handlePostcodeChange = e => setPostcode(e.target.value);
  const handleCityChange = e => setCity(e.target.value);

  // Form state
  const [idType, setIdType] = React.useState(data?.id_types?.[0] || { value: '', label: '' });
  const [id, setId] = React.useState('');
  const [taxNo, setTaxNo] = React.useState('');
  const [name, setName] = React.useState('');
  const [email, setEmail] = React.useState('');
  // Removed confirmEmail state
  const [phone, setPhone] = React.useState('');
  // Removed businessRegNoOld, msic, sstNo state
  const [address1, setAddress1] = React.useState('');
  const [address2, setAddress2] = React.useState('');
  const [postcode, setPostcode] = React.useState('');
  const [city, setCity] = React.useState('');
  const [state, setState] = React.useState(data?.states?.[0] || { value: '', label: '' });
  const [country, setCountry] = React.useState(data?.countries?.[0] || { value: '', label: '' });

  // Error states
  const [errId, setErrId] = React.useState('');
  const [errTaxNo, setErrTaxNo] = React.useState('');
  const [errName, setErrName] = React.useState('');
  const [errEmail, setErrEmail] = React.useState('');
  const [errPhone, setErrPhone] = React.useState('');
  // Removed errConfirmEmail, errMsic
  const [errAddress1, setErrAddress1] = React.useState('');
  const [errPostcode, setErrPostcode] = React.useState('');
  const [errCity, setErrCity] = React.useState('');
  const [errState, setErrState] = React.useState('');
  const [errCountry, setErrCountry] = React.useState('');

  // Modal states
  const [showModal, setShowModal] = React.useState(false);
  const [modalCode, setModalCode] = React.useState(1); // 1=success(old),2=error,3=new success
  const [isSuccess, setIsSuccess] = React.useState(false);
  const [successData, setSuccessData] = React.useState(null);
  const [errorMessage, setErrorMessage] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const handleSubmit = e => {
    e.preventDefault();
    if (isSubmitting) return;
    clearErrors();
    setIsSubmitting(true);

    const countryCode = phoneRef.current?.state?.country?.dialCode;
    const phoneNumber = phone.replace(`+${countryCode}`, '');
    const isValid = validateInvoiceForm({
      id,
      idType,
      taxNo,
      name,
      email,
      phoneNumber,
      address1,
      postcode,
      city,
      state,
      country,
      invNo: receiptNo,
      setErrorFns: {
        setErrId,
        setErrTaxNo,
        setErrName,
        setErrEmail,
        setErrPhone,
        setErrAddress1,
        setErrPostcode,
        setErrCity,
        setErrState,
        setErrCountry,
        setErrInvNo: () => {}, // Not used in this page
      },
    });
    if (!isValid) {
      setIsSubmitting(false);
      return;
    }

    submitForm();
  };

  const submitForm = async () => {
    let body = {
      id_type: idType.value,
      id_no: id,
      tax_identification_no: taxNo,
      personal_company_name: name,
      email: email,
      phone: phone,
      receipt_no: receiptNo,
      address_line_1: address1,
      address_line_2: address2,
      postcode: postcode,
      city: city,
      state: state.value,
      country: country.value,
    };

    // (Debug logs removed for production cleanliness)

    if (isQrMode && token) {
      body.token = token;
    }

    try {
      const response = await ApiInstance.post(ApiRoutes.POST_INVOICE, body);
      setIsSuccess(true);
      setSuccessData(response?.data?.data || null);
      setModalCode(3);
      setShowModal(true);
    } catch (error) {
      // Keep concise logging for troubleshooting (could integrate with external logger)
      // Safe environment check (avoids ReferenceError in browsers without process polyfill)
      try {
        if (
          typeof process !== 'undefined' &&
          process.env &&
          process.env.NODE_ENV !== 'production'
        ) {
          console.error('Submission failed', error);
        }
      } catch (_) {
        // Silently ignore env inspection errors
      }

      // Clear previous errors
      clearErrors();

      // Handle validation errors from backend
      if (error.response?.data?.errors) {
        const validationErrors = error.response.data.errors;

        // Track the first error field for scrolling
        let firstErrorField = null;

        // Map backend field names to frontend error setters
        if (validationErrors.id_no) {
          setErrId(
            Array.isArray(validationErrors.id_no)
              ? validationErrors.id_no.join(', ')
              : validationErrors.id_no
          );
          if (!firstErrorField) firstErrorField = 'id_no';
        }
        if (validationErrors.tax_identification_no) {
          setErrTaxNo(
            Array.isArray(validationErrors.tax_identification_no)
              ? validationErrors.tax_identification_no.join(', ')
              : validationErrors.tax_identification_no
          );
          if (!firstErrorField) firstErrorField = 'tax_identification_no';
        }
        if (validationErrors.personal_company_name) {
          setErrName(
            Array.isArray(validationErrors.personal_company_name)
              ? validationErrors.personal_company_name.join(', ')
              : validationErrors.personal_company_name
          );
          if (!firstErrorField) firstErrorField = 'personal_company_name';
        }
        if (validationErrors.email) {
          setErrEmail(
            Array.isArray(validationErrors.email)
              ? validationErrors.email.join(', ')
              : validationErrors.email
          );
          if (!firstErrorField) firstErrorField = 'email';
        }
        if (validationErrors.phone) {
          setErrPhone(
            Array.isArray(validationErrors.phone)
              ? validationErrors.phone.join(', ')
              : validationErrors.phone
          );
          if (!firstErrorField) firstErrorField = 'phone';
        }
        if (validationErrors.address_line_1) {
          setErrAddress1(
            Array.isArray(validationErrors.address_line_1)
              ? validationErrors.address_line_1.join(', ')
              : validationErrors.address_line_1
          );
          if (!firstErrorField) firstErrorField = 'address_line_1';
        }
        if (validationErrors.postcode) {
          setErrPostcode(
            Array.isArray(validationErrors.postcode)
              ? validationErrors.postcode.join(', ')
              : validationErrors.postcode
          );
          if (!firstErrorField) firstErrorField = 'postcode';
        }
        if (validationErrors.city) {
          setErrCity(
            Array.isArray(validationErrors.city)
              ? validationErrors.city.join(', ')
              : validationErrors.city
          );
          if (!firstErrorField) firstErrorField = 'city';
        }
        if (validationErrors.state) {
          setErrState(
            Array.isArray(validationErrors.state)
              ? validationErrors.state.join(', ')
              : validationErrors.state
          );
          if (!firstErrorField) firstErrorField = 'state';
        }
        if (validationErrors.country) {
          setErrCountry(
            Array.isArray(validationErrors.country)
              ? validationErrors.country.join(', ')
              : validationErrors.country
          );
          if (!firstErrorField) firstErrorField = 'country';
        }

        // Scroll to the first error field after a short delay
        if (firstErrorField) {
          setTimeout(() => {
            const fieldMap = {
              id_no: 'input[name="id_no"]',
              tax_identification_no: 'input[name="tax_identification_no"]',
              personal_company_name: 'input[name="personal_company_name"]',
              email: 'input[name="email"]',
              phone: 'input[name="phone"]',
              address_line_1: 'input[name="address_line_1"]',
              postcode: 'input[name="postcode"]',
              city: 'input[name="city"]',
              state: 'select[name="state"]',
              country: 'select[name="country"]',
            };

            const selector = fieldMap[firstErrorField];
            const element = document.querySelector(selector);
            if (element) {
              element.scrollIntoView({ behavior: 'smooth', block: 'center' });
              element.focus();
            }
          }, 100);
        }

        // We set a general guidance message but DO NOT open modal for inline validation
        setErrorMessage('Please correct the highlighted fields and try again.');
      } else {
        // For non-validation errors, show the generic error message via modal
        setErrorMessage(error.response?.data?.message || error.message);
        setModalCode(2);
        setShowModal(true);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const clearErrors = () => {
    setErrId('');
    setErrTaxNo('');
    setErrName('');
    setErrEmail('');
    setErrPhone('');
    // removed confirm email & msic error resets
    setErrAddress1('');
    setErrPostcode('');
    setErrCity('');
    setErrState('');
    setErrCountry('');
  };

  return (
    <MainContainer title={'Invoice Details'}>
      <div className="x-master">
        <div className="x-master-wrapper">
          <Header />
          <main className="x-main for-subpage">
            <div className="x-settings-wrapper">
              <div className="x-settings">
                <div className="section-form">
                  <div className="container-form-main">
                    <div className="form-column">
                      <div className="form-progress">
                        <h3>✅ Step 2 of 2: Complete Your Invoice Request</h3>
                      </div>

                      {/* Receipt Summary */}
                      <div className="receipt-summary">
                        <h4>✅ Receipt Verified Successfully!</h4>
                        <div className="receipt-info">
                          <span>
                            <strong>Receipt No:</strong> {receiptNo}
                          </span>
                        </div>
                        <button type="button" className="back-button" onClick={onBack}>
                          ← Back to Receipt Validation
                        </button>
                      </div>

                      {/* Personal/Company Information */}
                      <div className="form-section">
                        <h4>Personal/Company Information</h4>
                        <div className="form-row">
                          <div className="form-input">
                            <SelectInput
                              title="ID Type*"
                              list={data?.id_types || []}
                              defaultValue={idType}
                              onChange={opt => {
                                if (opt) {
                                  setIdType(opt);
                                  setId(''); // Clear ID field when changing type
                                  setErrId(''); // Clear any validation errors
                                }
                              }}
                            />
                          </div>
                        </div>

                        <div className="form-row-double">
                          <div className={`form-input ${errId ? 'has-error' : ''}`}>
                            <TextInput
                              title={`${idType?.label || 'ID'}*`}
                              name="id_no"
                              placeholder={(() => {
                                const type = idType?.value || idType?.label;
                                switch (type) {
                                  case 'nric':
                                  case 'NRIC':
                                    return 'e.g., 920322039876 (dashes added automatically)';
                                  case 'business_registration_number':
                                  case 'BUSINESS REGISTRATION NO. (BRN)':
                                    return 'e.g., 201945678901 (exactly 12 digits)';
                                  case 'passport':
                                  case 'PASSPORT':
                                    return 'e.g., A12345678';
                                  case 'army':
                                  case 'ARMY':
                                    return 'e.g., KOM111222333';
                                  default:
                                    return `Enter ${idType?.label?.toLowerCase() || 'ID'}`;
                                }
                              })()}
                              value={id}
                              onChange={handleIdChange}
                              error={errId}
                            />
                          </div>

                          <div className={`form-input ${errTaxNo ? 'has-error' : ''}`}>
                            <TextInput
                              title="Tax Identification No.*"
                              name="tax_identification_no"
                              placeholder="e.g., C26512228070"
                              value={taxNo}
                              onChange={handleTaxNoChange}
                              error={errTaxNo}
                            />
                          </div>
                        </div>

                        <div className="form-row">
                          <div className={`form-input ${errName ? 'has-error' : ''}`}>
                            <TextInput
                              title="Personal/Company Name*"
                              name="personal_company_name"
                              placeholder="Enter your full name or company name"
                              value={name}
                              onChange={e => setName(e.target.value)}
                              error={errName}
                            />
                          </div>
                        </div>

                        <div className="form-row-double">
                          <div className={`form-input ${errEmail ? 'has-error' : ''}`}>
                            <TextInput
                              title="Email*"
                              name="email"
                              placeholder="e.g., your.email@example.com"
                              value={email}
                              onChange={e => setEmail(e.target.value)}
                              error={errEmail}
                            />
                          </div>

                          <div className={`form-input ${errPhone ? 'has-error' : ''}`}>
                            <PhoneField
                              inputRef={phoneRef}
                              title="Phone Number*"
                              name="phone"
                              value={phone}
                              setValue={setPhone}
                              error={errPhone}
                            />
                          </div>
                        </div>
                      </div>

                      {/* Address Information */}
                      <div className="form-section">
                        <h4>Address Information</h4>
                        <div className="form-row">
                          <div className={`form-input ${errAddress1 ? 'has-error' : ''}`}>
                            <TextInput
                              title="Address Line 1*"
                              name="address_line_1"
                              placeholder="e.g., 123 Jalan Sultan, Taman Merdeka"
                              value={address1}
                              onChange={e => setAddress1(e.target.value)}
                              error={errAddress1}
                            />
                          </div>
                        </div>

                        <div className="form-row">
                          <div className="form-input">
                            <TextInput
                              title="Address Line 2"
                              name="address_line_2"
                              placeholder="Unit/Floor/Building name (optional)"
                              value={address2}
                              onChange={e => setAddress2(e.target.value)}
                            />
                          </div>
                        </div>

                        <div className="form-row-double">
                          <div className={`form-input ${errPostcode ? 'has-error' : ''}`}>
                            <TextInput
                              title="Postcode*"
                              name="postcode"
                              placeholder="e.g., 50480"
                              value={postcode}
                              onChange={e => setPostcode(e.target.value)}
                              error={errPostcode}
                            />
                          </div>

                          <div className={`form-input ${errCity ? 'has-error' : ''}`}>
                            <TextInput
                              title="City*"
                              name="city"
                              placeholder="e.g., Kuala Lumpur"
                              value={city}
                              onChange={e => setCity(e.target.value)}
                              error={errCity}
                            />
                          </div>
                        </div>

                        <div className="form-row-double">
                          <div className={`form-input ${errState ? 'has-error' : ''}`}>
                            <SelectInput
                              title="State*"
                              name="state"
                              list={data?.states || []}
                              defaultValue={state}
                              onChange={opt => opt && setState(opt)}
                              error={errState}
                            />
                          </div>

                          <div className={`form-input ${errCountry ? 'has-error' : ''}`}>
                            <SelectInput
                              title="Country*"
                              name="country"
                              list={data?.countries || []}
                              defaultValue={country}
                              onChange={opt => opt && setCountry(opt)}
                              error={errCountry}
                            />
                          </div>
                        </div>
                      </div>

                      <div className="form-row">
                        <div className="button-container">
                          <button
                            className="button-primary w-button"
                            onClick={handleSubmit}
                            disabled={isSubmitting}
                          >
                            {isSubmitting ? 'Submitting...' : 'Submit Invoice Request'}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>

      {showModal && (
        <>
          {modalCode === 1 && <Modal onClose={() => setShowModal(false)} />}
          {modalCode === 2 && (
            <ErrorModal onClose={() => setShowModal(false)} message={errorMessage} />
          )}
          {modalCode === 3 && (
            <SuccessModal
              receiptNo={receiptNo}
              message={successData?.message}
              onPrimary={() => {
                // Reset form and go back to step 1 for new receipt validation
                setId('');
                setTaxNo('');
                setName('');
                setEmail('');
                setPhone('');
                setAddress1('');
                setAddress2('');
                setPostcode('');
                setCity('');
                clearErrors();
                setShowModal(false);
                setIsSuccess(false);
                setSuccessData(null);
                if (typeof onBack === 'function') {
                  onBack();
                } else {
                  window.location.href = '/';
                }
              }}
            />
          )}
        </>
      )}

      <style>{`
        .receipt-summary {
          background-color: #e8f5e8;
          border: 1px solid #4caf50;
          border-radius: 8px;
          padding: 15px;
          margin-bottom: 25px;
        }

        .receipt-summary h4 {
          margin: 0 0 10px 0;
          color: #2e7d2e;
        }

        .receipt-info {
          display: flex;
          flex-direction: column;
          gap: 5px;
          margin-bottom: 15px;
        }

        .receipt-info span {
          color: #1b5e1f;
        }

        .back-button {
          background-color: #6c757d;
          color: white;
          border: none;
          padding: 8px 15px;
          border-radius: 4px;
          cursor: pointer;
          font-size: 14px;
        }

        .back-button:hover {
          background-color: #5a6268;
        }

        .form-section {
          margin-bottom: 30px;
        }

        .form-section h4 {
          margin: 0 0 20px 0;
          color: #333;
          border-bottom: 2px solid #007bff;
          padding-bottom: 5px;
        }

        /* Two-column layout for form fields */
        .form-row-double {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 20px;
          margin-bottom: 20px;
        }

        /* Error field highlighting */
        .form-input.has-error input,
        .form-input.has-error select,
        .form-input.has-error .phone-input-container input {
          border-color: #dc3545 !important;
          box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25) !important;
          background-color: #fff5f5 !important;
        }

        .form-input.has-error .input-label {
          color: #dc3545 !important;
        }

        /* Responsive layout - stack on mobile */
        @media (max-width: 768px) {
          .form-row-double {
            grid-template-columns: 1fr;
            gap: 15px;
          }
        }
      `}</style>
    </MainContainer>
  );
}

export default InvoiceForm;
